import { useEffect, useRef } from "react";

/**
 * About-page backdrop: the site's faint dot grid, with the Postern mark (an arch, a postern)
 * drifting across it as an aperture. Dots inside the aperture come into sharper
 * focus — larger and brass — echoing "money that was always there, just never
 * in view". On desktop the aperture eases toward the cursor.
 * Static single frame for prefers-reduced-motion; pauses when off-screen.
 */
const GAP = 26;

export default function ApertureBackdrop() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const host = canvas.parentElement;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const css = getComputedStyle(document.documentElement);
    const base = css.getPropertyValue("--line-2").trim() || "#d9d4ca";
    const brass = css.getPropertyValue("--brass").trim() || "#eaa221";
    const brassDark = css.getPropertyValue("--brass-dark").trim() || "#96690d";

    let w = 0, h = 0, dpr = 1, raf = 0, visible = true, t0 = performance.now();
    const lens = { x: 0, y: 0 };
    let mouse = null;

    function resize() {
      const r = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = r.width; h = r.height;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!lens.x) { lens.x = w * 0.72; lens.y = h * 0.3; }
      draw(0);
    }

    function draw(time) {
      if (!reduce) {
        const s = time / 1000;
        const tx = mouse ? mouse.x : w * (0.5 + 0.36 * Math.sin(s * 0.11));
        const ty = mouse ? mouse.y : h * (0.42 + 0.26 * Math.sin(s * 0.17 + 1.3));
        lens.x += (tx - lens.x) * (mouse ? 0.06 : 0.02);
        lens.y += (ty - lens.y) * (mouse ? 0.06 : 0.02);
      }
      ctx.clearRect(0, 0, w, h);
      // The aperture is the Postern mark: an arch (viewBox 0..32, arch x 8..24,
      // top semicircle centred at 16,14 with r 8, legs down to y 28), scaled so
      // its middle (16,17) sits at the lens point.
      const S = Math.max(2.5, Math.min(3.5, w / 360)); // tiny: about 50px wide on desktop
      const ox = lens.x - 16 * S, oy = lens.y - 17 * S;
      const edge = 2.4 * S; // how far inside the outline dots reach full focus
      for (let y = GAP / 2; y < h; y += GAP) {
        const fade = Math.max(0.35, 1 - y / (h * 1.1)); // gentle top-to-bottom fade
        for (let x = GAP / 2; x < w; x += GAP) {
          // signed distance (in px, negative = inside) to the arch shape
          const u = (x - ox) / S, v = (y - oy) / S;
          const dRect = Math.max(Math.abs(u - 16) - 8, Math.abs(v - 21) - 7);
          const dCirc = Math.hypot(u - 16, v - 14) - 8;
          const sd = Math.min(dRect, dCirc) * S;
          const k = sd < 0 ? Math.min(1, -sd / edge) : 0;
          const f = k * k * (3 - 2 * k); // smoothstep
          ctx.globalAlpha = fade * (1 - f);
          ctx.fillStyle = base;
          ctx.beginPath(); ctx.arc(x, y, 1.4, 0, Math.PI * 2); ctx.fill();
          if (f > 0) {
            ctx.globalAlpha = Math.max(fade, 0.6) * (0.2 + f * 0.5);
            ctx.fillStyle = brass;
            ctx.beginPath(); ctx.arc(x, y, 1.4 + f * 2.4, 0, Math.PI * 2); ctx.fill();
          }
        }
      }
      // the mark's outline — arch and tick — drawn thin and faint
      ctx.save();
      ctx.translate(ox, oy); ctx.scale(S, S);
      ctx.globalAlpha = 0.55; ctx.strokeStyle = brassDark;
      ctx.lineWidth = 1.6 / S; ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath(); ctx.moveTo(8, 28); ctx.lineTo(8, 14); ctx.arc(16, 14, 8, Math.PI, 0); ctx.lineTo(24, 28); ctx.stroke();
      ctx.lineWidth = 1.5 / S;
      // a small tick, like a door knob, right by the right-hand edge
      ctx.beginPath(); ctx.moveTo(20.4, 20.5); ctx.lineTo(21.2, 21.3); ctx.lineTo(22.6, 19.7); ctx.stroke();
      ctx.restore();
      ctx.globalAlpha = 1;
    }

    function loop(now) {
      if (visible && !document.hidden) draw(now - t0);
      raf = requestAnimationFrame(loop);
    }

    const ro = new ResizeObserver(resize); ro.observe(host);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(host);
    const fine = window.matchMedia("(pointer: fine)").matches;
    const onMove = (e) => { const r = host.getBoundingClientRect(); mouse = { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const onLeave = () => { mouse = null; };
    if (fine && !reduce) { host.addEventListener("pointermove", onMove); host.addEventListener("pointerleave", onLeave); }
    resize();
    if (!reduce) raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      host.removeEventListener("pointermove", onMove); host.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }} />;
}
